import React, { useMemo, useState } from 'react';
import axios from 'axios';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { ExternalLink } from 'lucide-react';

export interface AgreementMeta {
	key: string;
	url: string;
	effectiveDate?: string;
	version: string;
}

// White-labelled (reseller) accounts don't see the platform's brand in agreement names.
function getAgreementDisplayName(key: string, brandNeutral = false): string {
	const name = agreementName(key);
	return brandNeutral ? name.replace(/^Chekkit\s+/, '') : name;
}

function agreementName(key: string): string {
	switch (key) {
		case 'chekkit_pay_account_agreement':
			return 'Chekkit Pay Account Agreement';
		case 'voice_call_data':
			return 'Chekkit Voice: Data Processing Terms';
		case 'voice_compliance':
			return 'Chekkit Voice: Recording & Compliance Terms';
		case 'ai_platform_use_agreement':
			return 'AI Message Enhancement Agreement';
		case 'ai_employee_agreement':
			return 'Chekkit AI Employee Agreement';
		case 'hipaa_baa':
			return 'Chekkit HIPAA Business Associate Agreement';
		default:
			// Fallback: de-snake_case a bit for readability
			return key.replace(/_/g, ' ');
	}
}

function formatAgreementMeta(a: AgreementMeta): string {
	const parts: string[] = [];
	if (a.version) parts.push(`v${a.version}`);
	if (a.effectiveDate) parts.push(`Effective ${a.effectiveDate}`);
	return parts.join(' • ');
}

export function isAgreementRequiredError(err: any): {
	requiredAgreements: AgreementMeta[];
} | null {
	const status = err?.response?.status;
	const data = err?.response?.data;
	if (status !== 412) return null;
	if (!data || data.code !== 'AGREEMENT_REQUIRED') return null;
	if (!Array.isArray(data.requiredAgreements)) return null;
	return { requiredAgreements: data.requiredAgreements };
}

type Props = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	agreements: AgreementMeta[];
	source: string;
	onAccepted: () => Promise<void> | void;
	title?: string;
	description?: string;
	/** Leave the platform brand out of agreement names (reseller accounts). */
	brandNeutral?: boolean;
};

export default function AgreementAcceptanceDialog({
	open,
	onOpenChange,
	agreements,
	source,
	onAccepted,
	title = 'Agreement required',
	description = 'To continue, you must review and accept the following agreement(s).',
	brandNeutral = false,
}: Props) {
	const [checked, setChecked] = useState(false);
	const [saving, setSaving] = useState(false);

	const agreementKeys = useMemo(
		() => agreements.map((a) => a.key),
		[agreements],
	);

	const onContinue = async () => {
		if (!checked || saving) return;
		setSaving(true);
		try {
			await axios.post('/api/legal/accept-agreements', {
				agreementKeys,
				source,
			});
			await onAccepted();
			onOpenChange(false);
			setChecked(false);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[520px]">
				<DialogHeader>
					<DialogTitle className="max-sm:px-10 sm:max-md:pr-10 max-md:leading-snug">
						{title}
					</DialogTitle>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="space-y-2">
						{agreements.map((a) => (
							<div
								key={`${a.key}:${a.version}`}
								className="rounded-lg border border-border bg-muted/20 px-4 py-3"
							>
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0">
										<div className="text-sm font-medium leading-snug">
											{getAgreementDisplayName(a.key, brandNeutral)}
										</div>
										<div className="text-xs text-muted-foreground mt-1">
											{formatAgreementMeta(a)}
										</div>
									</div>
									<a
										href={a.url}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center gap-1 text-sm text-primary hover:underline underline-offset-4 shrink-0 max-md:-my-2.5 max-md:min-h-11 max-md:text-[15px]"
									>
										View
										<ExternalLink className="h-4 w-4" />
									</a>
								</div>
							</div>
						))}
					</div>

					<Separator />

					<label className="flex items-start gap-3 max-md:py-1.5">
						<Checkbox
							checked={checked}
							onCheckedChange={(v) => setChecked(v === true)}
						/>
						<span className="text-sm leading-snug max-md:text-[15px]">
							I have reviewed and agree to the agreement(s) above.
						</span>
					</label>
				</div>

				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={saving}
					>
						Cancel
					</Button>
					<Button onClick={onContinue} disabled={!checked || saving}>
						{saving ? 'Saving…' : 'Continue'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

